import React, { useState, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useAuth } from '../../../../wms/context/wms-context';
import { searchByValue, searchByValueNoSerialized } from '../../../../utils/commonAPICalls.js';
import LoadingSpinner from '../../../../components/LoadingSpinner';

function SearchField({
    label,
    value,
    valueName,
    onChange,
    required,
    tableName,
    readOnly,
    notSerialized,
    customQueryDefaults = [],
    includeAddRowButton = false,
}) {
    const { currentUser } = useAuth();

    const [filteredOptions, setFilteredOptions] = useState([]);
    const [apiData, setApiData] = useState([]);
    const [showOptions, setShowOptions] = useState(false);
    const [isSelected, setIsSelected] = useState(!!value);
    const [isInvalidInput, setIsInvalidInput] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [inputValue, setInputValue] = useState('');
    const [loadingInput, setLoadingInput] = useState(false);
    const [isErrorInputState, setIsErrorInputState] = useState(false);

    const listRef = useRef();
    const containerRef = useRef(null);
    const requestCounter = useRef(0);

    const closeDropdown = () => {
        setLoadingInput(false);
        setShowOptions(false);
    };

    const handleOutsideClick = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
            closeDropdown();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleInputChange = (e) => {
        setLoadingInput(true);
        const newInputValue = e.target.value ?? '';
        setInputValue(newInputValue);
        setIsSelected(false);
        setIsInvalidInput(false);
        if (!showOptions) setShowOptions(true);

        if (!newInputValue.trim()) {
            setIsInvalidInput(false);
            setIsSelected(false);
            setIsErrorInputState(false);
            closeDropdown();
            onChange({ target: { value: '', valueName: '' } });
            return;
        }
    };

    async function fetchSearchList() {
        requestCounter.current++;
        const currentRequest = requestCounter.current;

        const handler = setTimeout(async () => {
            try {
                const dataToSend = { tableName, value: inputValue };
                const response = notSerialized
                    ? await searchByValueNoSerialized(dataToSend, currentUser)
                    : await searchByValue(dataToSend, currentUser);

                if (currentRequest === requestCounter.current) {
                    let rows = response?.data ?? [];
                    if (includeAddRowButton) {
                        rows = [...rows, { Id: 'add_new', Name: `+ Create New Part` }];
                    }
                    setApiData(rows);
                    setFilteredOptions(rows);
                    if (rows.length > 0 && listRef.current) listRef.current.scrollTo(0);
                    if (rows.length === 0) closeDropdown();
                }
            } catch (error) {
                if (currentRequest === requestCounter.current) setLoadingInput(false);
                console.error('Error fetching data', error);
            } finally {
                setLoadingInput(false);
            }
        }, 500);

        return () => clearTimeout(handler);
    }

    useEffect(() => {
        if (inputValue == null) return;
        if (!inputValue.trim()) {
            setLoadingInput(false);
            return;
        }

        fetchSearchList();
    }, [inputValue, tableName, currentUser, notSerialized]);

    // Match input field to value
    useEffect(() => {
        if (includeAddRowButton) {
            if (value != 'add_new') setInputValue(valueName);

            if (inputValue == null) return;
            if (!inputValue.trim()) {
                setLoadingInput(false);
                return;
            }

            setLoadingInput(true);
            fetchSearchList();
        }
    }, [value]);

    useEffect(() => {
        if (!customQueryDefaults?.length) return;
        const matchingDefault = customQueryDefaults.find((q) => tableName === q.tableName);
        if (matchingDefault) {
            setIsErrorInputState(false);
            setInputValue(matchingDefault.Name || '');
            onChange({ target: { value: matchingDefault.Id || '', valueName: matchingDefault.Name || '' } });
            setIsInvalidInput(false);
            setIsSelected(true);
            setTimeout(() => {
                closeDropdown();
            }, 100);
        }
    }, []);

    const handleOptionClick = (option) => {
        setInputValue(option.Name);
        onChange({ target: { value: option.Id, valueName: option.Name } });

        if (label === 'Part Master') {
            currentUser.inscor__Serialized__c = option.inscor__Serialized__c;

            if (option.Id === 'add_new') {
                onChange({ target: { value: 'add_new', valueName: 'add_new', defaultValue: inputValue } });
                setInputValue('');
                setApiData([{ Id: 'add_new', Name: `+ Create New Part` }]);
                setFilteredOptions([{ Id: 'add_new', Name: `+ Create New Part` }]);
            }
        }
        setIsSelected(true);
        setIsInvalidInput(false);
        closeDropdown();
    };

    const handleKeyDown = (e) => {
        if (!filteredOptions?.length) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (highlightedIndex < filteredOptions.length - 1) {
                    setHighlightedIndex((prev) => prev + 1);
                    listRef.current?.scrollToItem(highlightedIndex + 1, 'center');
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (highlightedIndex > 0) {
                    setHighlightedIndex((prev) => prev - 1);
                    listRef.current?.scrollToItem(highlightedIndex - 1, 'center');
                }
                break;
            case 'Enter':
                if (highlightedIndex !== -1) {
                    handleOptionClick(filteredOptions[highlightedIndex]);
                }
                break;
            default:
                break;
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (inputValue?.trim() && !isSelected) {
                setIsInvalidInput(true);
            } else {
                setIsInvalidInput(false);
            }
            closeDropdown();
        }, 150);
    };

    useEffect(() => {
        const showError = Boolean(inputValue?.trim()) && !isSelected && !showOptions && !loadingInput;
        setIsErrorInputState(showError || isInvalidInput);
    }, [isInvalidInput, showOptions, isSelected, inputValue, loadingInput]);

    let inputClassName = 'mt-1 p-2 w-full border rounded-md';
    if (isSelected) inputClassName += ' ring-2 ring-blue-200';
    if (isErrorInputState) inputClassName += ' border-red-500 ring-2 ring-red-200';

    const Row = ({ index, style }) => (
        <div
            style={style}
            className={`flex justify-left items-center cursor-pointer text-left p-2 ${
                index === highlightedIndex ? 'bg-gray-300' : ''
            } hover:bg-gray-200`}
            onMouseDown={(e) => {
                e.preventDefault();
                handleOptionClick(filteredOptions[index]);
            }}
        >
            {filteredOptions[index].Name}
        </div>
    );

    return (
        <div className='relative px-8 my-2' ref={containerRef}>
            <label className='block text-sm font-medium text-gray-700'>{label}</label>
            <input
                type='text'
                value={inputValue}
                disabled={readOnly}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                required={required}
                className={inputClassName}
                onBlur={handleBlur}
                onFocus={() => {
                    setShowOptions(true);
                    setHighlightedIndex(-1); // Reset highlighted index on focus
                }}
                placeholder='Type to search…'
            />
            {required && <div className='absolute top-0 right-0 text-xs text-red-500'>required *</div>}

            {showOptions && apiData.length > 0 && !loadingInput && (
                <div className='absolute z-50 mt-2 w-full max-h-96 bg-white rounded-md border border-gray-300 shadow-lg overflow-scoll'>
                    <List
                        ref={listRef}
                        height={Math.min(300, (filteredOptions?.length || 0) * 60)}
                        itemCount={filteredOptions?.length || 0}
                        itemSize={60}
                        width='100%'
                        className='z-50'
                    >
                        {Row}
                    </List>
                </div>
            )}

            {loadingInput && (
                <div className='absolute z-50 pb-16 mt-2 w-full max-h-96 bg-white rounded-md border border-gray-300 shadow-lg overflow-scoll'>
                    <LoadingSpinner />
                </div>
            )}

            {isErrorInputState && inputValue?.length && !showOptions ? (
                <p className='text-sm text-red-600'>
                    Please select an item from the dropdown or remove the text from this field
                </p>
            ) : null}
        </div>
    );
}

export default SearchField;
